import {
  WEBSITE_PAGES_ACTION,
  websitePageRows,
  websitePagesAsked,
  websitePagesExact,
  websitePagesBlocked,
  websitePagesRefOf,
  type WebsitePagesRead,
  type WebsitePagesRef,
} from "./websitePagesCard";
import { AGGREGATE_DATASET_ACTION, aggregateDatasetCard } from "./aggregateDatasetCard";
import { START_CODING_JOB_TOOL, codingJobCard } from "./codingJobCard";
import {
  COMPONENT_RELEASE_ACTION,
  componentReleaseBlocked,
  componentReleaseRefOf,
  type ComponentReleaseRead,
  type ComponentReleaseRef,
} from "./componentReleaseCard";
import {
  settingsCatalogDefinition,
  type SettingsCatalogDefinition,
} from "@cleo/contracts/settings-catalog";
import { MEMBER_CHANGE_STATUS_WORDS } from "@cleo/shared/memberChangeCard";
import { instantFrom, validTimeZone } from "@cleo/shared/wallClock";
import {
  ACTOR_APPEARANCE_KEYS,
  parseActorAppearance,
  type ActorAppearancePreferences,
} from "@neucleos/theme";
import type { CanvasShareDataRead, CanvasShareDataRef } from "./canvasShareClient";
import { canvasShareIncludes } from "./canvasShareWords";
import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
import { useNotificationSnapshots } from "./notificationObserver";
import type { SubscriptionPlanRead } from "./subscriptionPlanClient";
import {
  commercialChangeConsequence,
  commercialChangeRows,
  type CommercialChangeRead,
} from "./commercialChangeClient";
import {
  memberApprovalNeedsTouchId,
  memberCardReceiptFor,
  memberChangeRows,
  spendMemberCardReceipt,
  type MemberChangeRead,
} from "./memberChangeClient";
import {
  CONSULTING_TAX_POLICIES,
  CONSULTING_TAX_POLICY_CARD_WORDS,
  type ConsultingTaxPolicy,
} from "../../../../../../backend/convex/lib/consultingTaxPolicies.js";
import {
  MEETING_LINK_AUDIENCE_WORDS,
  checkMeetingUrl,
} from "../../../../../../backend/convex/lib/meetingUrl.js";
import type { ConsultingAudience, ConsultingCatalogRead } from "./consultingClient";
import type { ApprovalCall } from "@cleo/contracts";
import { approvalCallOf } from "./approvalCall";
import {
  joinedWords,
  sentence,
  shareAudienceOf,
  shareRightsOf,
  shareUntil,
} from "@cleo/shared/canvasShareWords";
import { plainModelAccessCall } from "@cleo/shared/modelAccessWords";
import { plainCanvasWrite } from "@cleo/shared/canvasWriteWords";

// The person's human-in-the-loop rail (2026-09-03, register §42 D-GT12): the
// Cleo Delegation gate instances awaiting THIS Actor in the World, read
// through the local server's loopback lane, and the person's decision on
// one of them. Every failure arrives as the lane's typed refusal envelope
// and is surfaced as { ok: false, kind, reason } — never thrown. Rows are
// the World authority's; nothing here stores or re-derives standing.

export type GateInstanceState = "ALL" | "CREATED" | "DECIDED" | "SATISFIED";
export type GateDecisionOutcome = "approved" | "declined";

export interface GateInstanceRow {
  readonly notificationReadState?: "unread" | "read" | "dismissed";
  readonly gateKey: string;
  readonly gateInstanceKey: string;
  readonly targetDigest: string;
  readonly title: string;
  readonly kind: string;
  readonly deciderClass: string;
  readonly state: string;
  readonly outcome: string | null;
  readonly humanDecides: boolean;
  readonly needsAttention?: boolean;
  readonly decider?: string;
  readonly effectStatus?: string;
  /** Why the World cancelled this request ("stopped": the person pressed Stop while it waited), when it did. */
  readonly invalidatedReason?: string;
  /** The World's own sentence for a cancelled request, shown on its card in place of Approve. */
  readonly cancelledSentence?: string;
  /**
   * W-16dc173f: this Desk recorded the request as stopped (the person pressed Stop while Cleo's call waited on it),
   * whether or not the World could be told. Its card offers only Decline.
   */
  readonly stoppedHere?: boolean;
  /**
   * Round 2 (P2-g): this Desk's stop record cannot be read (or was found damaged), so nothing shows that this
   * request, opened in one of its AI SDK conversations, was not stopped. Its card offers only Decline.
   */
  readonly stopUnconfirmed?: boolean;
  readonly threadId: string | null;
  readonly sessionId: string | null;
  readonly createdAt: number;
  /**
   * Can 73: the stored change to who belongs to the World this gate is a step of (the change's own gate, or a
   * Remove's delete, an invitation's send). The World marks the row; its Approve waits on that change's card.
   */
  readonly memberChangeKey?: string;
  /** The World's mark: approved only on its card (lib/worldAuth.js projectGateInstance cardApproved). */
  readonly cardApproved?: boolean;
  /** Can 47: what the card says, read by the World when it opened this approval (backend lib/approvalCardFacts.js). */
  readonly cardFacts?: unknown;
  /** W-7d543e69: the exact call Cleo asked about, from her own approval
   * record on this Mac (joined by the gate's key); absent for a gate opened
   * on another device. */
  readonly request?: GateApprovalRequest;
}

export interface GateApprovalRequest {
  readonly detail: string;
  readonly conversationTitle: string | null;
  /**
   * W-dc12e842: what Approve lets run, as the Desk recorded it beside the request (worldDoorHttp
   * approvalRequestsForGates). Null for a record without it (an older Desk): the card is general, its text shown.
   */
  readonly call?: ApprovalCall | null;
  /**
   * W-dc12e842: true when this is an engine's approval request (worldDoorHttp approval.requested), which is
   * approvable only when its structured call shows it in full. A World gate instance's own request (a call the World
   * held, a settings card) or a gate with no record here is described from the World's own record instead.
   */
  readonly engineRequest?: boolean;
}

/**
 * What a card is built from: the request's text, shown and never parsed for identity, and the structured call the
 * adapter recorded from the engine's protocol (W-dc12e842). Only the call says which tool runs, on whose server.
 */
export interface ApprovalRequestInput {
  readonly detail: string;
  readonly call?: ApprovalCall | null;
}

/**
 * The Desk's own tool call a request is, or null: only a structured tool call whose adapter established that it
 * comes from the Desk's own registered server instance (deskOwnServer, never a server's or tool's name). Its plain
 * words, its folder card and its stored-change card are built from this alone.
 */
export function deskOwnToolCall(call: ApprovalCall | null | undefined): {
  readonly tool: string;
  readonly args: unknown;
  readonly preview: { readonly title: string; readonly lines: ReadonlyArray<string> } | null;
} | null {
  if (!call || call.kind !== "tool" || call.deskOwnServer !== true) return null;
  if (typeof call.tool !== "string" || call.tool === "") return null;
  return { tool: call.tool, args: call.args, preview: call.preview ?? null };
}

export interface GateLaneRefusal {
  readonly ok: false;
  readonly kind: string;
  readonly reason: string;
}

export type GateInstanceListOutcome =
  | {
      readonly ok: true;
      readonly rows: ReadonlyArray<GateInstanceRow>;
      readonly nextCursor?: string | null;
      readonly attentionCount?: number;
    }
  | GateLaneRefusal;

/**
 * #1730 (W-9bc79724 as ruled 9 Oct 2026): the invitation link the founder's own approval made, answered by the Desk's
 * decide route to this one Approve only. It is shown once (InvitationLinkOnceHost) and kept only in this window's
 * memory: never stored, never sent to a conversation or to Cleo. "shown-before": a retried approval, which shows nothing.
 * "not-made" (Can 73, P5b): no link was made, in the World's words (a seat-buying link whose payment failed, or whose
 * seat was bought and its link refused), and what was charged; it never carries a link.
 */
export interface InvitationLinkShownOnce {
  readonly status: "made" | "shown-before" | "not-made";
  readonly sentence: string;
  readonly email: string | null;
  readonly expiresAt: number | null;
  readonly url: string | null;
}

export type GateInstanceDecideOutcome =
  | {
      readonly ok: true;
      readonly row: GateInstanceRow | null;
      readonly invitationLink?: InvitationLinkShownOnce;
    }
  | GateLaneRefusal;

/** The decide route's memberLink, field by field; anything else is no link. */
export function parsedInvitationLink(value: unknown): InvitationLinkShownOnce | null {
  if (typeof value !== "object" || value === null) return null;
  const link = value as Record<string, unknown>;
  const status = link["status"];
  if (
    (status !== "made" && status !== "shown-before" && status !== "not-made") ||
    typeof link["sentence"] !== "string"
  )
    return null;
  const url = link["url"];
  return {
    status,
    sentence: link["sentence"],
    email: typeof link["email"] === "string" ? link["email"] : null,
    expiresAt: typeof link["expiresAt"] === "number" ? link["expiresAt"] : null,
    url: status === "made" && typeof url === "string" && url.startsWith("https://") ? url : null,
  };
}

function refusal(response: Response, body: Record<string, unknown> | null): GateLaneRefusal {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string" ? body["reason"] : "Gate instances are unavailable.",
  };
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown> | GateLaneRefusal> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  return body;
}

function isRefusal(value: Record<string, unknown> | GateLaneRefusal): value is GateLaneRefusal {
  return value["ok"] === false;
}

/**
 * The lane's row, tolerated field by field — the three keys are required
 * (a row without them is a refusal, never a guess); the lane serves the
 * rest as "" or null when the instance carries none (GT9, 497a2fad), so
 * those read as honest defaults: an empty state takes the list's own
 * state, a missing createdAt sinks the row to the end (0).
 */
export function parsedGateInstanceRow(
  value: unknown,
  fallbackState: string = "CREATED",
): GateInstanceRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row["gateKey"] !== "string") return null;
  if (typeof row["gateInstanceKey"] !== "string") return null;
  if (typeof row["targetDigest"] !== "string") return null;
  const str = (key: string): string | null =>
    typeof row[key] === "string" && row[key] !== "" ? (row[key] as string) : null;
  return {
    ...(["unread", "read", "dismissed"].includes(String(row["notificationReadState"]))
      ? { notificationReadState: row["notificationReadState"] as "unread" | "read" | "dismissed" }
      : {}),
    gateKey: row["gateKey"],
    gateInstanceKey: row["gateInstanceKey"],
    targetDigest: row["targetDigest"],
    // W-4022cbfa: a row the World served without a title reads as a plain request, never its gate's id.
    title: str("title") ?? GATE_UNTITLED_WORDS,
    kind: str("kind") ?? "gate",
    deciderClass: str("deciderClass") ?? "human",
    state: str("state") ?? fallbackState,
    outcome: str("outcome"),
    humanDecides: row["humanDecides"] === true,
    needsAttention:
      typeof row["needsAttention"] === "boolean"
        ? row["needsAttention"]
        : row["state"] === "CREATED" && row["humanDecides"] === true,
    decider: str("decider") ?? "self",
    effectStatus: str("effectStatus") ?? "not-recorded",
    ...(str("invalidatedReason") !== null ? { invalidatedReason: str("invalidatedReason")! } : {}),
    ...(str("cancelledSentence") !== null ? { cancelledSentence: str("cancelledSentence")! } : {}),
    ...(row["stoppedHere"] === true ? { stoppedHere: true } : {}),
    ...(row["stopUnconfirmed"] === true ? { stopUnconfirmed: true } : {}),
    threadId: str("threadId"),
    sessionId: str("sessionId"),
    createdAt: typeof row["createdAt"] === "number" ? row["createdAt"] : 0,
    ...(str("memberChangeKey") !== null ? { memberChangeKey: str("memberChangeKey")! } : {}),
    ...(row["cardApproved"] === true ? { cardApproved: true } : {}),
    ...(row["cardFacts"] !== undefined && row["cardFacts"] !== null
      ? { cardFacts: row["cardFacts"] }
      : {}),
    ...(parsedApprovalRequest(row["request"]) ?? {}),
  };
}

/** W-4022cbfa: what a request the World served without a title is called on its card and in the list. */
export const GATE_UNTITLED_WORDS = "Cleo asks for your decision";

function parsedApprovalRequest(value: unknown): { request: GateApprovalRequest } | null {
  if (typeof value !== "object" || value === null) return null;
  const request = value as Record<string, unknown>;
  // W-dc12e842: a request carries its text, its structured call, or both (a reasonless Codex file change has no text).
  const call = approvalCallOf(request["call"]);
  const detail = typeof request["detail"] === "string" ? request["detail"] : "";
  if (detail === "" && call === null) return null;
  return {
    request: {
      detail,
      call,
      ...(request["engineRequest"] === true ? { engineRequest: true } : {}),
      conversationTitle:
        typeof request["conversationTitle"] === "string" && request["conversationTitle"] !== ""
          ? request["conversationTitle"]
          : null,
    },
  };
}

/**
 * What a call will do, in the person's words: a headline, then what, with
 * whom, which rights and until when (W-f9a102e0: a Canvas share). Null for a
 * call not described here; the person then reads its exact fields.
 */
export interface PlainApprovalRequest {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  /** W-d729ba9b: the exact characters behind the plain words (a website's pages), one tap away: "Show exact changes". */
  readonly exact?: ReadonlyArray<string>;
  /** The Canvas the call names, so the Inbox can name it (describedApprovalRequest's canvasName). */
  readonly canvasId: string | null;
  /** W-6082d348: the conversation a message goes to, so its people can be named (recipients). */
  readonly conversationId?: string | null;
  /** W-96c0e692: the CRM contact or company a write changes, so it can be named (recordName). */
  readonly crmRecord?: { readonly kind: "contact" | "company"; readonly id: string } | null;
  /** W-96c0e692: a follow-up named only by its id; its contact or company is read through it (recordName). */
  readonly crmFollowUp?: { readonly activityId: string } | null;
  /** W-96c0e692: the calendar occurrence a move or removal names, so its event can be named (recordName). */
  readonly calendarOccurrence?:
    | { readonly calendarEventId: string; readonly originalStartAt: number }
    | { readonly calendarEventId: string; readonly occurrenceDate: string }
    | null;
  /** Stopping a World's subscription renewing: the stored plan the call names, read by the surface. */
  readonly subscriptionPlan?: { readonly planKey: string; readonly snapshotDigest: string } | null;
  /** A booking type whose meeting link a call changes, so the card can name it and its link now (bookingType). */
  readonly bookingType?: { readonly bookingTypeId: string } | null;
  /** Can 68: a change to what Neucleos charges, the stored change the call names, read by the surface. */
  readonly commercialChange?: { readonly changeKey: string; readonly digest: string } | null;
  /** Can 73: a change to who belongs to a World, the stored change the call names, read by the surface. */
  readonly memberChange?: { readonly changeKey: string; readonly digest: string } | null;
  /** PR #1625: an audience change names the catalog card it was read from (its audience and digest), read by the surface. */
  readonly consultingCatalog?: {
    readonly audience: ConsultingAudience;
    readonly catalogDigest: string;
  } | null;
  /** W-7cdf5337: the member or group a Canvas share names, so it can be named (shareRecipient). */
  readonly shareRecipient?:
    | { readonly kind: "actor"; readonly actorId: string }
    | { readonly kind: "group"; readonly groupId: string }
    | null;
  /** W-7cdf5337: the exact Components-and-data preview a Canvas share names, read by the surface (canvasShareData). */
  readonly canvasShareData?: CanvasShareDataRef | null;
  /** The website pages card: the exact set of pages a publication names, read by the surface (websitePages). */
  readonly websitePages?: WebsitePagesRef | null;
  /** Step 3 K2c2: the release, builds and digest a platform Components publication names, read by the surface. */
  readonly componentRelease?: ComponentReleaseRef | null;
  /** Step 3 I2: the aggregate dataset card shows this call whole (aggregateDatasetCard.ts); set only when it can. */
  readonly aggregateDataset?: true | null;
  /** Can 81: the coding job card, built from the call itself (codingJobCard.ts): approvable here when it shows it. */
  readonly codingJob?: boolean;
  /**
   * Why Approve must not be possible, in the person's words, or null when it
   * may be. Set while the facts are being read, when the read was refused or
   * the plan is not the one the call names.
   */
  readonly approvalBlocked?: string | null;
  /** A decision for this exact call alone: no "always allow" answer exists. */
  readonly perCallOnly?: boolean;
}

/**
 * What the World's records say about the things a call names, read by the
 * surface that shows it (useApprovalRequestContext): the Canvas by name, the
 * people a message reaches by name, and the World by name. Null or absent:
 * not read (yet), and the words stay general rather than guess.
 */
/** A booking type read for a card (approvalRequestContext useBookingTypeRead). */
export type BookingTypeRead =
  | { readonly status: "loaded"; readonly title: string; readonly meetingUrl: string | null }
  | { readonly status: "failed" };

export interface ApprovalRequestContext {
  /** The person's own messages in the conversation that asks (newest last), as typed: the card's "You asked". */
  readonly askedWords?: ReadonlyArray<string> | null;
  readonly canvasName?: string | null;
  /** Everyone but the person who will receive the message; [] when no one else is in it. */
  readonly recipients?: ReadonlyArray<string> | null;
  readonly worldName?: string | null;
  /** The stored cancellation plan a subscription call names; absent until the surface has asked. */
  readonly subscriptionPlan?: SubscriptionPlanRead | null;
  /** Can 68: the stored change to what Neucleos charges that an apply call names; absent until asked. */
  readonly commercialChange?: CommercialChangeRead | null;
  /** Can 73: the stored change to who belongs to the World that an apply call names; absent until asked. */
  readonly memberChange?: MemberChangeRead | null;
  /** PR #1625: the consultant's catalog card, as the World writes it now, for an audience change; absent until asked. */
  readonly consultingCatalog?: ConsultingCatalogRead | null;
  /** W-96c0e692: the CRM contact or company, or the calendar event, the call names, by name. */
  readonly recordName?: string | null;
  /**
   * W-4022cbfa: the calendar event a removal or move names, as the person's calendar lists it: its
   * title, its time, and whether it repeats. Absent or null: not read, and nothing is said of a series.
   */
  readonly calendarEvent?: ApprovalCalendarEvent | null;
  /** W-7cdf5337: the member, or the group (and its size), a Canvas share names, by name. */
  readonly shareRecipient?: { readonly name: string; readonly memberCount?: number | null } | null;
  /**
   * W-7cdf5337: the Components and data a Canvas share with data gives, as the
   * World lists them now; absent until the surface has asked.
   */
  readonly canvasShareData?: CanvasShareDataRead | null;
  /** The website pages card: each path changed, added or kept, as the World reads them now; absent until asked. */
  readonly websitePages?: WebsitePagesRead | null;
  /** Step 3 K2c2: the World's review of the platform Components release the call names; absent until asked. */
  readonly componentRelease?: ComponentReleaseRead | null;
  /**
   * The booking type a meeting-link change names, as the host's own list carries it now: its title
   * and the link it sends today. Absent: not asked; failed: the list could not be read or does not hold it.
   */
  readonly bookingType?: BookingTypeRead | null;
  /** The person's IANA zone, the one every time is shown in; absent: this device's zone. */
  readonly timeZone?: string;
  /** Can 47: what the card says, read by the World when it opened the approval (GateInstanceRow.cardFacts). */
  readonly cardFacts?: unknown;
}

// The share words (who, what they may do, until when) are one set for the Desk and Pocket (Can 47): @cleo/shared/canvasShareWords.

/** W-7cdf5337: why a share with data cannot be approved while what it gives is not listed. */
export const CANVAS_SHARE_DATA_READING =
  "Reading which Components and data come with this share. Approve unlocks when they have loaded.";
export const CANVAS_SHARE_DATA_CHANGED =
  "This Canvas's Components or data changed after Cleo listed them, so the World would refuse this share. Ask Cleo to list them again.";
export const CANVAS_SHARE_DATA_NO_REVISION =
  "This request does not name the Canvas revision its Components and data were listed from, so the World would refuse it. Ask Cleo to list them again.";

function plainCanvasShare(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest | null {
  const canvasName = context.canvasName ?? null;
  const canvasId = typeof input["canvasId"] === "string" ? input["canvasId"] : null;
  const actions = Array.isArray(input["actions"]) ? input["actions"] : null;
  if (canvasId === null || actions === null || actions.length === 0) return null;
  const audience = shareAudienceOf(input, context.shareRecipient ?? null);
  if (audience === null) return null;
  const { shareRecipient } = audience;
  const rights = shareRightsOf(actions);
  const until = shareUntil(input);
  if (rights === null || until === null) return null;
  const what = canvasName ? `the Canvas “${canvasName}”` : "a Canvas";
  // W-7cdf5337: what sharing its data gives, exactly: whole collections (or a
  // person's own rows), now and later, also outside this Canvas, for as long
  // as the share lasts; and changes only with a share that can edit the Canvas
  // (W-d2731d4e: the World refuses changes with a share that cannot, so such a
  // call is not described as if it could run).
  const digest = typeof input["dependencyDigest"] === "string" ? input["dependencyDigest"] : null;
  const withData = digest !== null;
  const dataChanges = withData && input["dataChanges"] === true;
  if (dataChanges && !actions.includes("edit")) return null;
  const sequence = input["sequence"];
  const canvasShareData: CanvasShareDataRef | null =
    digest !== null &&
    typeof sequence === "number" &&
    Number.isSafeInteger(sequence) &&
    sequence >= 1
      ? { canvasId, sequence, dataChanges, digest }
      : null;
  // Each Component, collection and snapshot by name, in the Desk's Share
  // dialog's words, from the World's own list for this revision, and only
  // when that list is the one the call names (the World refuses any other).
  // Until it is, Approve waits and the card says why.
  const read = context.canvasShareData ?? { status: "reading" as const };
  const listed =
    canvasShareData !== null && read.status === "loaded" && read.digest === canvasShareData.digest
      ? read.dependencies
      : null;
  const blocked = !withData
    ? null
    : canvasShareData === null
      ? CANVAS_SHARE_DATA_NO_REVISION
      : read.status === "reading"
        ? CANVAS_SHARE_DATA_READING
        : read.status === "failed"
          ? read.reason
          : listed === null
            ? CANVAS_SHARE_DATA_CHANGED
            : null;
  const also =
    !withData || listed !== null
      ? []
      : [
          "they may also use the Components it uses and read the data it uses: every row and field of each collection, or only their own rows where a collection keeps rows private, now and later, also outside this Canvas, for as long as this share lasts",
          ...(dataChanges
            ? [
                "they may also add, change and delete any row of the collections its data actions write to, also outside this Canvas",
              ]
            : []),
        ];
  const includes =
    listed === null ? blocked : canvasShareIncludes(canvasShareData!.sequence, listed);
  return {
    headline: `Share ${what} with ${audience.headline}`,
    rows: [
      ["What", sentence(`${what}, in every version, including later ones`)],
      ["With", audience.with],
      ["They may", [rights, ...also].map(sentence).join(" ")],
      ...(includes === null ? [] : [["Includes", includes] as const]),
      ["Until", until],
    ],
    canvasId,
    ...(shareRecipient === null ? {} : { shareRecipient }),
    ...(withData ? { canvasShareData } : {}),
    // Nothing approves it, for this call or the session, until it is listed.
    ...(blocked === null ? {} : { approvalBlocked: blocked, perCallOnly: true }),
  };
}

/** W-4022cbfa: one calendar event's occurrence, as the person's calendar lists it. */
export interface ApprovalCalendarEvent {
  readonly title: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly recurring: boolean;
}

/**
 * The website pages card (websitePagesCard.ts): publishing several pages of a website at once, each path changed
 * (every character removed and added), added or unchanged, and every other path kept. Approve waits for the World's
 * read of these exact pages; a call this card cannot show exactly is described as unshowable and cannot be approved.
 */
function plainWebsitePages(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const ref = websitePagesRefOf(input);
  if (ref === null)
    return {
      headline: "Publish pages of your website",
      rows: [
        [
          "What",
          "This publication's pages could not be read from the request, so it is not shown here.",
        ],
      ],
      canvasId: null,
      approvalBlocked:
        "This card cannot show these pages exactly, so they cannot be approved here.",
      perCallOnly: true,
    };
  const read = context.websitePages ?? { status: "reading" as const };
  const count = ref.pages.length === 1 ? "1 page" : `${ref.pages.length} pages`;
  if (read.status !== "loaded")
    return {
      headline: `Publish ${count} of your website`,
      rows: [["Pages", ref.pages.map((page) => page.path).join(", ")]],
      canvasId: null,
      websitePages: ref,
      approvalBlocked: websitePagesBlocked(ref, read),
      perCallOnly: true,
    };
  const keptWords = [
    read.kept.length ? read.kept.join(", ") : "no other pages",
    read.files ? `${read.files} file${read.files === 1 ? "" : "s"}` : null,
    read.redirects ? `${read.redirects} redirect${read.redirects === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join("; ");
  return {
    headline: `Publish ${count} of ${read.hostname}`,
    rows: [
      [
        "Who sees it",
        `Everyone who opens https://${read.hostname}. The site goes from release ${read.sequence} to ${read.sequence + 1}.`,
      ],
      ...(websitePagesAsked(context.askedWords)
        ? [["You asked", websitePagesAsked(context.askedWords)!] as const]
        : []),
      ...read.pages.flatMap(websitePageRows),
      ["Kept as they are", `${keptWords}. Nothing is taken down.`],
    ],
    ...(websitePagesExact(read.pages).length ? { exact: websitePagesExact(read.pages) } : {}),
    canvasId: null,
    websitePages: ref,
    approvalBlocked: websitePagesBlocked(ref, read),
    perCallOnly: true,
  };
}

/**
 * Step 3 K2c2 (#1844): the platform Components release card (componentReleaseCard.ts). The World's own lines, verbatim
 * and in order, then Added, Changed and Kept by name; Approve only while the World's review of these builds has the
 * request's digest. A call this card cannot show exactly is described as unshowable and cannot be approved.
 */
function plainComponentRelease(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const headline = "Publish the platform's Components to the Library";
  const ref = componentReleaseRefOf(input);
  if (ref === null)
    return {
      headline,
      rows: [["What", "This release could not be read from the request, so it is not shown here."]],
      canvasId: null,
      approvalBlocked: "This card cannot show this release exactly, so it cannot be approved here.",
      perCallOnly: true,
    };
  const read = context.componentRelease ?? { status: "reading" as const };
  const blocked = componentReleaseBlocked(ref, read);
  if (read.status !== "loaded")
    return {
      headline,
      rows: [["Components", ref.builds.map((build) => build.slot).join(", ")]],
      canvasId: null,
      componentRelease: ref,
      approvalBlocked: blocked,
      perCallOnly: true,
    };
  const listed = (names: ReadonlyArray<string>) => (names.length ? names.join(", ") : "none");
  return {
    headline: read.lines[0]!,
    rows: [
      ...read.lines.slice(1).map((line) => ["", line] as const),
      ["Added", listed(read.added)],
      ["Changed", listed(read.changed)],
      ["Kept", listed(read.kept)],
    ],
    canvasId: null,
    componentRelease: ref,
    approvalBlocked: blocked,
    perCallOnly: true,
  };
}

/**
 * W-e0e51d23: a link anyone can open to one exact revision of a Canvas (the
 * World's friend-links site, backend canvasFriendLinks.js), as the person
 * reads it: which Canvas and revision, who can open it, and how to take it back.
 */
function plainCanvasFriendLink(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest | null {
  const canvasId = typeof input["canvasId"] === "string" ? input["canvasId"] : null;
  const sequence = input["sequence"];
  if (canvasId === null || typeof sequence !== "number" || !Number.isSafeInteger(sequence))
    return null;
  const canvasName = context.canvasName ?? null;
  const what = canvasName ? `the Canvas “${canvasName}”` : "a Canvas";
  return {
    headline: `Make a link anyone can open to ${what}`,
    rows: [
      [
        "What",
        `Revision ${sequence} of ${what}. Later changes to the Canvas do not reach the link.`,
      ],
      ["Who", "Anyone who has the link, with no account. Whoever has it can pass it on to others."],
      [
        "Undo",
        "Remove the link from the Canvas's shared links at any time; its page then stops opening.",
      ],
    ],
    canvasId,
  };
}

/**
 * W-4022cbfa: a new Component (an interactive card Cleo builds), as the person
 * reads it: its name and, when the call carries one, its description. The
 * call names no room or Canvas (a Component is kept among the person's own
 * Components and placed on a Canvas by a later step), so Where says that and
 * nothing more. Its source and declared inputs stay in the exact request.
 */
function plainComponentCreate(input: Record<string, unknown>): PlainApprovalRequest | null {
  const name = typeof input["name"] === "string" ? input["name"].trim() : "";
  if (name === "") return null;
  const description = typeof input["description"] === "string" ? input["description"].trim() : "";
  const shown = approvalFieldText(name);
  return {
    headline: `Make a new Component “${shown}”`,
    rows: [
      [
        "What",
        description === ""
          ? `A new Component named “${shown}”.`
          : `“${shown}”: ${approvalFieldText(description)}`,
      ],
      ["Where", "Saved to your Components. It is not placed on any Canvas by this step."],
    ],
    canvasId: null,
  };
}

/** Cleo Can 16: the Desk tool that saves a file the person attached into their own Media. */
export const SAVE_ATTACHMENT_TOOL = "save_attachment_to_media";

/** "2.4 MB", "830 KB", "512 bytes": a file's size as a person reads it. */
export function fileSizeWords(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "an unknown size";
  if (bytes < 1000) return `${bytes} bytes`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(bytes < 10_000_000 ? 1 : 0)} MB`;
}

function plainAttachmentToMedia(input: Record<string, unknown>): PlainApprovalRequest | null {
  if (input["action"] !== "save") return null;
  const name = typeof input["name"] === "string" ? input["name"].trim() : "";
  const mediaType = typeof input["mediaType"] === "string" ? input["mediaType"] : "";
  const size = typeof input["byteLength"] === "number" ? input["byteLength"] : NaN;
  if (name === "" || mediaType === "") return null;
  const kind = mediaType.startsWith("image/")
    ? "photo"
    : mediaType.startsWith("video/")
      ? "video"
      : mediaType.startsWith("audio/")
        ? "audio file"
        : "file";
  const shown = approvalFieldText(name);
  return {
    headline: `Save the ${kind} “${shown}” to your Media`,
    rows: [
      ["What", `“${shown}”, the ${kind} you attached (${mediaType}, ${fileSizeWords(size)}).`],
      [
        "Where",
        "Your Media in this World, private to you. You can then use it in a Canvas; nothing is shared or published.",
      ],
    ],
    canvasId: null,
  };
}

/** The Desk tool that changes one of the person's settings (server heldCalls.ts SET_SETTING_TOOL). */
export const SET_SETTING_TOOL = "set_setting";

/** "fontSizeInterface" as a person says it: "interface font size"; "confirmThreadDelete": "confirm thread delete". */
const settingWords = (key: string): string =>
  key
    .replace(/^(profile|world)\./u, "")
    .replace(
      /^(fontSize|fontFamily)(\w+)$/u,
      (_, kind: string, what: string) => `${what}${kind === "fontSize" ? "FontSize" : "Font"}`,
    )
    .split(".")
    .join(" ")
    .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .replace(/E164$/u, "")
    .toLowerCase()
    .trim();

/**
 * Names for the settings a person keeps in a World, where the key's own words
 * read wrongly ("presence visibility", "receipts default").
 */
const WORLD_SETTING_WORDS: Readonly<Record<string, string>> = {
  "world.presence.visibility": "presence",
  "world.receipts.default": "read receipts",
  "world.theme.accent": "accent colour",
};

/**
 * Whose setting it is, from the settings catalog (never the key's prefix): a
 * World-wide setting is "this World's …"; the person's own setting kept in a
 * World is "your … in this World"; anything else is "your …".
 */
function settingOwner(key: string, definition: SettingsCatalogDefinition | null): string {
  const words = WORLD_SETTING_WORDS[key] ?? settingWords(key);
  if (definition?.custody.owner === "world") return `this World's ${words}`;
  if (definition?.lane === "world") return `your ${words} in this World`;
  return `your ${words}`;
}

/** "5 minutes", "30 seconds", "250 milliseconds": a duration the catalog keeps in milliseconds. */
function durationWords(ms: number): string {
  const unit = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;
  if (ms !== 0 && ms % 3_600_000 === 0) return unit(ms / 3_600_000, "hour");
  if (ms !== 0 && ms % 60_000 === 0) return unit(ms / 60_000, "minute");
  if (ms % 1_000 === 0) return unit(ms / 1_000, "second");
  return unit(ms, "millisecond");
}

/**
 * A setting's new value in words, in the catalog's own units: a duration in
 * seconds or minutes, a switch as on or off, a choice by its name, a text in
 * quotes. An object (an accent colour, a record) is never shown as its JSON:
 * the exact request below keeps it.
 */
function settingValueWords(
  key: string,
  value: unknown,
  definition: SettingsCatalogDefinition | null,
): string {
  if (value === null) return "cleared";
  if (typeof value === "boolean") return value ? "on" : "off";
  const kind = definition?.type.kind;
  if (kind === "duration-ms" && typeof value === "number" && Number.isFinite(value))
    return durationWords(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return kind === "enum" ? value : `“${approvalFieldText(value)}”`;
  if (Array.isArray(value) && value.every((item) => typeof item === "string"))
    return value.length === 0 ? "none" : value.map((item) => `“${item}”`).join(", ");
  return key === "world.theme.accent" ? "a new accent colour" : "a new value";
}

/**
 * W-4022cbfa: a change to one setting, as the person reads it: which setting,
 * whose, and its new value (the call's own key and value, never a gate id).
 */
function plainSettingChange(input: Record<string, unknown>): PlainApprovalRequest | null {
  const key = typeof input["key"] === "string" && input["key"] !== "" ? input["key"] : null;
  if (key === null || !Object.hasOwn(input, "value")) return null;
  const value = input["value"];
  const definition = settingsCatalogDefinition(key);
  const whose = settingOwner(key, definition);
  const shown = settingValueWords(key, value, definition);
  const described = typeof value === "object" && value !== null && !Array.isArray(value);
  return {
    headline:
      value === null
        ? `Clear ${whose}`
        : typeof value === "boolean"
          ? `Turn ${whose} ${shown}`
          : described
            ? `Change ${whose}`
            : `Change ${whose} to ${shown}`,
    rows: [
      ["Setting", sentence(whose)],
      [
        "New value",
        described && shown === "a new value"
          ? "A new value. The exact request below shows it."
          : sentence(shown),
      ],
    ],
    canvasId: null,
  };
}

/** The World tool that saves the person's appearance choices (backend mcpProtocol.js SAVE_APPEARANCE_TOOL). */
export const SAVE_APPEARANCE_TOOL = "neucleos_save_appearance";
/** Each appearance choice as the Appearance settings name it. */
export const APPEARANCE_CHOICE_WORDS: Readonly<Record<keyof ActorAppearancePreferences, string>> = {
  density: "Display density",
  ambientMotion: "Ambient motion",
  toneDownSections: "Tone down colors",
  shellGlass: "Panel glass",
  glassOpacity: "Glass opacity",
  lightStudy: "Light study",
  lightIntensity: "Light intensity",
  lightBreathe: "Breathing light",
  hoverLight: "Hover light",
  opaqueDoors: "Opaque doors",
  doorFade: "Door fade",
  cardGlow: "Card glow",
};
const PERCENT_CHOICES: ReadonlySet<string> = new Set(["glassOpacity", "cardGlow"]);

/**
 * W-91f56203: an appearance save, as the person reads it, from the call's own arguments only. The World replaces
 * every choice with the call's preferences (backend actorAppearance.ts save, through parseActorAppearance), so the
 * card is plain only when the call names every choice with a value the World saves as given; anything missing,
 * unknown or out of range keeps the general words and the exact request.
 */
function plainAppearanceSave(input: Record<string, unknown>): PlainApprovalRequest | null {
  const keys = Object.keys(input);
  const updatedAt = input["updatedAt"];
  const preferences = input["preferences"];
  if (
    keys.length !== 2 ||
    !keys.includes("preferences") ||
    !keys.includes("updatedAt") ||
    (updatedAt !== null && (typeof updatedAt !== "number" || !Number.isFinite(updatedAt))) ||
    typeof preferences !== "object" ||
    preferences === null ||
    Array.isArray(preferences)
  )
    return null;
  const asked = preferences as Record<string, unknown>;
  const saved = parseActorAppearance(asked);
  const named = Object.keys(asked);
  if (
    named.length !== ACTOR_APPEARANCE_KEYS.length ||
    ACTOR_APPEARANCE_KEYS.some((key) => !Object.hasOwn(asked, key) || asked[key] !== saved[key])
  )
    return null;
  const words = (key: keyof ActorAppearancePreferences): string => {
    const value = saved[key];
    if (typeof value === "boolean") return value ? "On." : "Off.";
    if (typeof value === "number") return PERCENT_CHOICES.has(key) ? `${value}%.` : `${value}.`;
    return sentence(value);
  };
  return {
    headline: "Save your appearance choices",
    rows: [
      ...ACTOR_APPEARANCE_KEYS.map((key) => [APPEARANCE_CHOICE_WORDS[key], words(key)] as const),
      ["Where", "Saved with your account, for every World and device you use."],
    ],
    canvasId: null,
  };
}

/** This device's zone: the person's, as the CRM and calendar screens use it. */
export const deviceTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;
const zoned = (instant: number, timeZone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(instant);

/**
 * W-96c0e692: a time a call names, as a date and time in the person's zone,
 * never a bare epoch number. A local time Cleo gave in another zone is also
 * said as she gave it, so a mistaken zone shows. Null: not a time.
 */
export function approvalTimeText(
  value: unknown,
  personZone: string = deviceTimeZone(),
): string | null {
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  const at = instantFrom(value, "time");
  if (!at.ok) return null;
  const zone = validTimeZone(personZone) ? personZone : "UTC";
  const shown = `${zoned(at.instant, zone)} (${zone})`;
  const given =
    typeof value === "object" && value !== null
      ? (value as { timeZone?: unknown }).timeZone
      : undefined;
  return typeof given === "string" && given !== zone
    ? `${shown}, which is ${zoned(at.instant, given)} in ${given}`
    : shown;
}

/** Who a CRM write is about, by name; while unread, never its record id. */
const crmTargetRow = (
  record: { readonly kind: "contact" | "company" } | null,
  name: string | null,
): readonly [string, string] => [
  record?.kind === "company" ? "Company" : "Contact",
  name !== null
    ? `${name}.`
    : record?.kind === "company"
      ? "A company in this World. Its name could not be read."
      : "A contact in this World. Their name could not be read.",
];

/**
 * W-96c0e692: a CRM write as the person reads it: who it is about, by name,
 * and each time as a date and time in their zone. Null for an action not
 * described here; the person then reads its exact fields.
 */
function plainCrmWrite(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest | null {
  const action = input["action"];
  const contactId = typeof input["contactId"] === "string" ? input["contactId"] : null;
  const companyId = typeof input["companyId"] === "string" ? input["companyId"] : null;
  const crmRecord =
    contactId !== null
      ? ({ kind: "contact", id: contactId } as const)
      : companyId !== null
        ? ({ kind: "company", id: companyId } as const)
        : null;
  const name = context.recordName ?? null;
  const who = name ?? (crmRecord?.kind === "company" ? "a company" : "a contact");
  const zone = context.timeZone ?? deviceTimeZone();
  const body = typeof input["body"] === "string" ? input["body"] : null;
  if (action === "recordActivity" && crmRecord !== null && body !== null) {
    if (input["kind"] === "note")
      return {
        headline: `Add a note to ${who}`,
        rows: [crmTargetRow(crmRecord, name), ["Text", `“${body}”`]],
        canvasId: null,
        crmRecord,
      };
    if (input["kind"] !== "follow-up") return null;
    const due = approvalTimeText(input["dueAt"], zone);
    if (due === null) return null;
    return {
      headline: `Add a follow-up for ${who}`,
      rows: [crmTargetRow(crmRecord, name), ["Due", `${due}.`], ["Text", `“${body}”`]],
      canvasId: null,
      crmRecord,
    };
  }
  const activityId = typeof input["activityId"] === "string" ? input["activityId"] : null;
  if (action === "setFollowUpReminder" && (crmRecord !== null || activityId !== null)) {
    // Cleo may name the follow-up alone; whose it is is then read through it.
    const named =
      crmRecord !== null
        ? { crmRecord }
        : { crmRecord: null, crmFollowUp: { activityId: activityId! } };
    const forWhom = crmTargetRow(crmRecord ?? { kind: "contact" }, name);
    if (input["remindAt"] === null)
      return {
        headline: `Cancel your reminder about a follow-up for ${who}`,
        rows: [forWhom, ["Then", "No reminder is shown for it."]],
        canvasId: null,
        ...named,
      };
    const when = approvalTimeText(input["remindAt"], zone);
    if (when === null) return null;
    return {
      headline: `Remind you about a follow-up for ${who}`,
      rows: [
        forWhom,
        ["When", `${when}.`],
        [
          "Then",
          "Cleo shows you the reminder then. It replaces an earlier one for this follow-up.",
        ],
      ],
      canvasId: null,
      ...named,
    };
  }
  // W-7287425d: moving an open follow-up says the new time in the person's zone.
  if (action === "rescheduleActivity" && crmRecord !== null) {
    const due = approvalTimeText(input["dueAt"], zone);
    if (due === null) return null;
    return {
      headline: `Move a follow-up for ${who} to a new time`,
      rows: [
        crmTargetRow(crmRecord, name),
        ["New due time", `${due}.`],
        [
          "Then",
          "The follow-up is due then instead. Its earlier time stays in its history; a reminder keeps its own time.",
        ],
      ],
      canvasId: null,
      crmRecord,
    };
  }
  if (action === "completeActivity" && crmRecord !== null && typeof input["outcome"] === "string")
    return {
      headline: `Mark a follow-up for ${who} as done`,
      rows: [crmTargetRow(crmRecord, name), ["Outcome", `“${input["outcome"]}”`]],
      canvasId: null,
      crmRecord,
    };
  if (action === "saveContact") {
    const changes =
      typeof input["changes"] === "object" &&
      input["changes"] !== null &&
      !Array.isArray(input["changes"])
        ? (input["changes"] as Record<string, unknown>)
        : null;
    if (changes === null) return null;
    const changed = Object.entries(changes).map(([key, value]) =>
      value === null ? `${key}: cleared` : `${key}: ${approvalFieldText(value)}`,
    );
    if (contactId === null) {
      const newName = typeof changes["name"] === "string" ? changes["name"] : null;
      return {
        headline: newName !== null ? `Add the contact “${newName}”` : "Add a contact",
        rows: [["Details", changed.length > 0 ? changed.join("; ") : "None."]],
        canvasId: null,
        crmRecord: null,
      };
    }
    return {
      headline: `Change the contact ${who}`,
      rows: [
        crmTargetRow(crmRecord, name),
        ["Changes", changed.length > 0 ? changed.join("; ") : "None."],
      ],
      canvasId: null,
      crmRecord,
    };
  }
  return null;
}

/**
 * W-96c0e692: a calendar write as the person reads it: the event by name, each
 * time in their zone. Each calendar tool is named once, at its own branch;
 * any other tool is null (no second list of tool names: the law of Cleo's
 * actions, law 10).
 */
function plainCalendarWrite(
  tool: string,
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest | null {
  const zone = context.timeZone ?? deviceTimeZone();
  const minutes = typeof input["durationMinutes"] === "number" ? input["durationMinutes"] : null;
  const title = typeof input["title"] === "string" ? input["title"] : null;
  if (tool === "neucleos_create_calendar_event") {
    const when = approvalTimeText(input["startsAt"], zone);
    if (when === null || title === null) return null;
    return {
      headline: `Add “${title}” to your calendar`,
      rows: [
        ["When", `${when}.`],
        ...(minutes !== null ? [["Length", `${minutes} minutes.`] as const] : []),
        ...(typeof input["recurrence"] === "string"
          ? [["Repeats", `${input["recurrence"]}, in ${String(input["timeZone"])}.`] as const]
          : []),
      ],
      canvasId: null,
    };
  }
  const calendarEventId =
    typeof input["calendarEventId"] === "string" ? input["calendarEventId"] : null;
  // W-794cc790: an occurrence named by its date (YYYY-MM-DD in the meeting's own zone), its original start, or both.
  const date =
    typeof input["occurrenceDate"] === "string"
      ? occurrenceDateText(input["occurrenceDate"])
      : null;
  const hasStart = input["originalStartAt"] !== undefined;
  const original = hasStart ? instantFrom(input["originalStartAt"], "originalStartAt") : null;
  const startText = hasStart ? approvalTimeText(input["originalStartAt"], zone) : null;
  if (calendarEventId === null || (hasStart && (!original?.ok || startText === null))) return null;
  if (!hasStart && date === null) return null;
  const from = startText ?? `The occurrence on ${date}`;
  const calendarOccurrence = original?.ok
    ? { calendarEventId, originalStartAt: original.instant }
    : { calendarEventId, occurrenceDate: String(input["occurrenceDate"]) };
  const named: ReadonlyArray<readonly [string, string]> =
    startText !== null && date !== null ? [["Date named", `${date}.`]] : [];
  // W-4022cbfa: the event as the person's calendar lists it, when it was read. A series is named
  // only when the event repeats; an event not read says nothing of one.
  const known = context.calendarEvent ?? null;
  // The exact occurrence's title only (readCalendarOccurrence): never another occurrence's.
  const name = known?.title ?? context.recordName ?? null;
  const event = name !== null ? `“${name}”` : "a meeting";
  const span = known !== null ? eventSpanText(known.startsAt, known.endsAt, zone) : null;
  if (tool === "neucleos_exclude_calendar_instance") {
    if (known !== null && span !== null && !known.recurring)
      return {
        headline: `Delete ${event} on ${span}`,
        rows: [
          ["When", `${approvalTimeText(known.startsAt, zone) ?? from}.`],
          ["Then", "The event is deleted from your calendar."],
        ],
        canvasId: null,
        calendarOccurrence,
      };
    if (known !== null && span !== null)
      return {
        headline: `Remove ${event} on ${span}`,
        rows: [
          ["Occurrence", `${from}.`],
          ...named,
          ["Then", "Only this occurrence is removed; the rest of the series stays."],
        ],
        canvasId: null,
        calendarOccurrence,
      };
    // The exact occurrence was not read (a failed read, or one moved far from its original time):
    // the call still names one occurrence, so the card says one, never the whole event.
    return {
      headline: `Remove one occurrence of ${event} from your calendar`,
      rows: [["Occurrence", `${from}.`], ...named, ["Then", "Only this occurrence is removed."]],
      canvasId: null,
      calendarOccurrence,
    };
  }
  if (tool !== "neucleos_override_calendar_instance") return null;
  const to = approvalTimeText(input["startsAt"], zone);
  if (to === null) return null;
  return {
    headline:
      known?.recurring === true && span !== null
        ? `Move ${event} on ${span}, one occurrence of a repeating event`
        : span !== null
          ? `Move ${event} on ${span}`
          : `Move one occurrence of ${event} to a new time`,
    rows: [
      ["From", `${from}.`],
      ...named,
      ["To", `${to}.`],
      ...(minutes !== null ? [["Length", `${minutes} minutes.`] as const] : []),
      ...(title !== null ? [["New title", `“${title}”`] as const] : []),
      [
        "Then",
        known?.recurring === true
          ? "Only this occurrence moves; the rest of the series stays."
          : "It moves to the new time.",
      ],
    ],
    canvasId: null,
    calendarOccurrence,
  };
}

/**
 * W-4022cbfa: an event's day and time span as a person says it, in their zone:
 * "Wednesday 7 October, 9:00–9:30"; an event that ends on a later day names both days.
 */
export function eventSpanText(startsAt: number, endsAt: number, personZone: string): string {
  const zone = validTimeZone(personZone) ? personZone : "UTC";
  const parts = (instant: number) => {
    const read = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      read.find((item) => item.type === type)?.value ?? "";
    return {
      day: `${part("weekday")} ${Number(part("day"))} ${part("month")}`,
      time: `${Number(part("hour"))}:${part("minute")}`,
    };
  };
  const start = parts(startsAt);
  if (!Number.isFinite(endsAt) || endsAt <= startsAt) return `${start.day}, ${start.time}`;
  const end = parts(endsAt);
  return end.day === start.day
    ? `${start.day}, ${start.time}–${end.time}`
    : `${start.day}, ${start.time} – ${end.day}, ${end.time}`;
}

/** "Tuesday, November 10, 2026" for a real YYYY-MM-DD date; null for anything else. */
function occurrenceDateText(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) return null;
  const noon = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  if (new Date(noon).toISOString().slice(0, 10) !== value) return null;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(noon);
}

/** Fields that hold a time: shown as a date and time beside the exact value (W-96c0e692). */
const TIME_FIELDS = new Set([
  "dueAt",
  "remindAt",
  "startsAt",
  "originalStartAt",
  "expiresAt",
  "from",
  "to",
  "asOf",
]);

/** What a surface says while it reads the names a call uses (useApprovalRequestContext). */
export const APPROVAL_READING_WORDS = "Reading who and what this request names…";

/**
 * W-6082d348 (HG-COMM-SEND-01: the conversation's people, the World and the
 * exact text): a message Cleo asks to send, as the person reads it. The text
 * is the call's own, whole and unedited; who receives it comes from the
 * conversation's members (context.recipients), never from the model's prose.
 */
function plainMessageSend(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
  preview: { readonly title: string } | null,
): PlainApprovalRequest | null {
  const conversationId =
    typeof input["conversationId"] === "string" && input["conversationId"] !== ""
      ? input["conversationId"]
      : null;
  const text = typeof input["body"] === "string" ? input["body"] : null;
  // A text too long for the request line stands there as its length only
  // (AiSdkAdapter approvalDetail); it is not the exact text, so it is not described.
  if (conversationId === null || text === null || /^\[[\d,]+ characters\]$/.test(text)) return null;
  const recipients = context.recipients ?? null;
  const worldName = context.worldName ?? null;
  const who =
    recipients === null
      ? null
      : recipients.length === 0
        ? "no one else: you are the only person in this conversation"
        : joinedWords(recipients);
  return {
    // The runtime's own preview (withActionPreview: read from the World's
    // records under the same grant) names them when this Desk has not.
    headline:
      who !== null
        ? `Send a message to ${who} in ${worldName ?? "this World"}`
        : (preview?.title ??
          `Send a message in one of your conversations in ${worldName ?? "this World"}`),
    rows: [
      [
        "To",
        who === null
          ? "The people in this conversation. Their names could not be read."
          : recipients?.length === 0
            ? sentence(who)
            : `${who}.`,
      ],
      ["In", worldName !== null ? `${worldName}.` : "This World."],
      ["Text", `“${text}”`],
      ["Then", "They are notified. A sent message cannot be unsent."],
    ],
    canvasId: null,
    conversationId,
  };
}

/** Longest a field's text runs before it is cut, so Approve and Decline stay in reach. */
export const APPROVAL_FIELD_MAX_CHARACTERS = 240;
const characters = (count: number): string => `${count.toLocaleString("en-US")} characters`;
/** A file a call writes, as the person reads it: its path and size, never its contents. */
function fileLine(item: Record<string, unknown>): string | null {
  const path = typeof item["path"] === "string" ? item["path"] : null;
  if (path === null) return null;
  const body = [item["text"], item["content"], item["base64"], item["data"]].find(
    (part): part is string => typeof part === "string",
  );
  return body === undefined ? path : `${path} (${characters(body.length)})`;
}
/**
 * One argument's text: a list of files by path and size (a Canvas create or
 * save carries whole files), a plain list joined, and anything longer than
 * APPROVAL_FIELD_MAX_CHARACTERS cut with its full length named. The exact
 * request stays folded below for anyone who wants every byte.
 */
export function approvalFieldText(value: unknown): string {
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === "object" && item !== null && !Array.isArray(item))
  ) {
    const files = value.map((item) => fileLine(item as Record<string, unknown>));
    if (files.every((line): line is string => line !== null))
      return approvalFieldText(files.join(", "));
  }
  const text =
    Array.isArray(value) && value.every((item) => typeof item !== "object" || item === null)
      ? value.join(", ")
      : typeof value === "string"
        ? value
        : JSON.stringify(value);
  return text.length <= APPROVAL_FIELD_MAX_CHARACTERS
    ? text
    : `${text.slice(0, APPROVAL_FIELD_MAX_CHARACTERS).trimEnd()}… (${characters(text.length)} in all)`;
}

export const SUBSCRIPTION_SCHEDULE_TOOL = "neucleos_schedule_world_subscription_cancellation";
/** The fixed human gate for stopping a World's subscription renewing (backend gateCatalog). */
export const SUBSCRIPTION_CANCEL_GATE_KEY = "HG-COMMERCE-OPERATE-02";
export const SUBSCRIPTION_FACTS_UNAVAILABLE =
  "This card does not carry the exact cancellation it would approve, so it cannot be approved here. Ask Cleo to make the request again.";

/** A gate the World approves only on its card, which this Desk cannot show for this request. */
export const CARD_ONLY_UNAVAILABLE =
  "This is approved only on its own card on the Desk, which cannot be shown for this request, so it cannot be approved here.";
/**
 * Why Approve must be unavailable on a card, or null. A card for stopping a
 * subscription renewing (named by its gate, or by its call) is approvable only
 * when it shows the exact stored plan the call names; without those facts
 * (no request detail on this device, an unreadable call) it is blocked.
 */
export function subscriptionApprovalBlocked(
  gateKey: string | null,
  request: ApprovalRequestInput | null,
  plain: PlainApprovalRequest | null,
  memberChangeKey: string | null = null,
  cardApproved = false,
): string | null {
  // W-dc12e842: the tool a call names is its structured identity, never words in its text.
  const tool = calledTool(request?.call);
  // Can 73: every gate a change to who belongs to the World raises (marked by the World with the change's key) is
  // approvable only on that change's card, shown loaded, matching and waiting; on another device that may be never.
  if (memberChangeKey !== null) {
    if (plain?.approvalBlocked != null) return plain.approvalBlocked;
    if (plain?.memberChange?.changeKey !== memberChangeKey) return MEMBER_CHANGE_UNAVAILABLE;
  }
  // Can 73: a change to who belongs to a World is approvable only when its card shows the stored change itself. The
  // call is known by its gate or by the tool it names, never by those words appearing inside another call's text.
  if (gateKey === WORLD_MEMBERS_GATE_KEY || tool === APPLY_MEMBER_CHANGE_TOOL)
    return plain?.approvalBlocked != null
      ? plain.approvalBlocked
      : plain?.memberChange != null
        ? null
        : MEMBER_CHANGE_UNAVAILABLE;
  // Can 68: a change to what Neucleos charges is approvable only when its card shows the stored change itself.
  if (gateKey === COMMERCE_PRICE_GATE_KEY || tool === APPLY_COMMERCIAL_CHANGE_TOOL)
    return plain?.approvalBlocked != null
      ? plain.approvalBlocked
      : plain?.commercialChange != null
        ? null
        : COMMERCIAL_CHANGE_UNAVAILABLE;
  // The website pages card, and any gate the World marks card-only: approvable only when this card shows the exact
  // set, read and matching; a marked gate this Desk cannot show on its card is never approvable here.
  if (
    cardApproved ||
    plain?.websitePages != null ||
    plain?.componentRelease != null ||
    plain?.aggregateDataset === true ||
    plain?.codingJob === true
  )
    return plain?.approvalBlocked != null
      ? plain.approvalBlocked
      : plain?.websitePages != null ||
          plain?.componentRelease != null ||
          plain?.aggregateDataset === true ||
          plain?.codingJob === true
        ? null
        : CARD_ONLY_UNAVAILABLE;
  const names = gateKey === SUBSCRIPTION_CANCEL_GATE_KEY || tool === SUBSCRIPTION_SCHEDULE_TOOL;
  if (!names) return plain?.approvalBlocked ?? null;
  if (plain?.approvalBlocked != null) return plain.approvalBlocked;
  return plain?.subscriptionPlan != null ? null : SUBSCRIPTION_FACTS_UNAVAILABLE;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** A date as the server words it: day, month, year, in UTC. */
export function subscriptionDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function subscriptionMoney(cents: number, currency: string): string {
  const whole = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return currency.toLowerCase() === "usd" ? `$${grouped}` : `${grouped} ${currency.toUpperCase()}`;
}

export const APPLY_COMMERCIAL_CHANGE_TOOL = "neucleos_apply_commercial_change";
export const COMMERCE_PRICE_GATE_KEY = "HG-COMMERCE-PRICE-01";
const COMMERCIAL_CHANGE_UNAVAILABLE =
  "This device cannot show the prices this change sets, so it cannot be approved here. Open Commercial Operations → Prices.";

/**
 * Can 68: a change to what Neucleos charges, as the person reads it. Every line is the stored change's, read from the
 * World (context.commercialChange), the same lines the Prices tab's card shows. Until it has loaded, matches the call,
 * is still waiting and has not expired, Approve is blocked and the reason is said.
 */
function plainCommercialChange(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const changeKey = typeof input["changeKey"] === "string" ? input["changeKey"] : null;
  const digest = typeof input["digest"] === "string" ? input["digest"] : null;
  const base = { canvasId: null, perCallOnly: true } as const;
  const ref = changeKey !== null && digest !== null ? { changeKey, digest } : null;
  const blocked = (headline: string, why: string): PlainApprovalRequest => ({
    ...base,
    headline,
    rows: [["Why you cannot approve yet", why]],
    commercialChange: ref,
    approvalBlocked: why,
  });
  if (ref === null)
    return blocked(
      "Change what Neucleos charges",
      "This request does not name a prepared change, so it cannot be shown or approved.",
    );
  const read = context.commercialChange ?? { status: "reading" as const };
  if (read.status === "reading")
    return blocked(
      "Reading the new prices from Neucleos…",
      "Reading the new prices from Neucleos. Approve unlocks when they have loaded.",
    );
  if (read.status === "failed")
    return blocked(
      "Cannot show the new prices",
      `${read.reason} Nothing can be approved until they can be read.`,
    );
  const { change } = read;
  if (change.changeKey !== ref.changeKey || change.digest !== ref.digest)
    return blocked(
      "Cannot show the new prices",
      "The prepared change is not the one this request names. Ask Cleo to prepare it again.",
    );
  if (change.status === "applied")
    return blocked(
      "These prices are already in effect",
      "This change was already applied; there is nothing left to approve.",
    );
  if (change.closed?.reason === "declined")
    return blocked(
      "This change was declined",
      "This change was declined, so it is closed and never applied. Ask Cleo again to prepare a new card.",
    );
  if (change.closed !== undefined || change.expiresAt <= Date.now())
    return blocked(
      "Cannot show the new prices",
      "This prepared change has expired. Ask Cleo to prepare it again.",
    );
  return {
    ...base,
    headline: change.title ?? "Change what Neucleos charges",
    rows: [
      ...commercialChangeRows(change),
      ["When you approve", commercialChangeConsequence(change.kind)],
    ],
    commercialChange: ref,
    approvalBlocked: null,
  };
}

export const APPLY_MEMBER_CHANGE_TOOL = "neucleos_apply_member_change";
/**
 * W-dc12e842: the tool a request calls, from its structured identity (any server's: a stored change's card then
 * keeps Approve blocked until it shows), or null for a command, a file change, a permission or an older record.
 */
export function calledTool(call: ApprovalCall | null | undefined): string | null {
  return call?.kind === "tool" && typeof call.tool === "string" && call.tool !== ""
    ? call.tool
    : null;
}
export const WORLD_MEMBERS_GATE_KEY = "HG-WORLD-GOVERN-02";
export const MEMBER_CHANGE_UNAVAILABLE =
  "This device cannot show who this change affects, so it cannot be approved here. Update Cleo on the Desk.";
const MEMBER_CHANGE_HEADLINE = "Change who belongs to this World";

/**
 * Can 73: a change to who belongs to a World, as the founder reads it (docs/world-members-design.md 5.4). Every line
 * is the stored change's, read from the World (context.memberChange), the "When you approve" line included. Until it
 * has loaded, matches the call, is still waiting and has not expired, Approve is blocked and the reason is said.
 */
function plainMemberChange(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const changeKey = typeof input["changeKey"] === "string" ? input["changeKey"] : null;
  const digest = typeof input["digest"] === "string" ? input["digest"] : null;
  const base = { canvasId: null, perCallOnly: true } as const;
  const ref = changeKey !== null && digest !== null ? { changeKey, digest } : null;
  const blocked = (headline: string, why: string): PlainApprovalRequest => ({
    ...base,
    headline,
    rows: [["Why you cannot approve yet", why]],
    memberChange: ref,
    approvalBlocked: why,
  });
  if (ref === null)
    return blocked(
      MEMBER_CHANGE_HEADLINE,
      "This request does not name a prepared change, so it cannot be shown or approved.",
    );
  // The card is bound to the change's key and digest alone; a call carrying anything else is not the one it shows.
  if (Object.keys(input).some((name) => name !== "changeKey" && name !== "digest"))
    return blocked(
      MEMBER_CHANGE_HEADLINE,
      "This request carries more than the prepared change it names, so it cannot be approved. Ask Cleo to prepare it again.",
    );
  const read = context.memberChange ?? { status: "reading" as const };
  if (read.status === "reading")
    return blocked(
      "Reading this change from Neucleos…",
      "Reading who this change affects from Neucleos. Approve unlocks when it has loaded.",
    );
  if (read.status === "failed")
    return blocked(
      "Cannot show this change",
      `${read.reason} Nothing can be approved until it can be read.`,
    );
  const { change } = read;
  if (change.changeKey !== ref.changeKey || change.digest !== ref.digest)
    return blocked(
      "Cannot show this change",
      "The prepared change is not the one this request names. Ask Cleo to prepare it again.",
    );
  if (change.status !== "proposed")
    return blocked(
      change.title,
      change.status === "refused"
        ? memberChangeRefusedWords(change)
        : MEMBER_CHANGE_STATUS_WORDS[change.status],
    );
  if (change.expiresAt <= Date.now())
    return blocked(
      "Cannot show this change",
      "This prepared change has expired. Ask Cleo to prepare it again.",
    );
  return {
    ...base,
    headline: change.title,
    rows: memberChangeRows(change),
    memberChange: ref,
    approvalBlocked: null,
  };
}
/**
 * Why a refused change was refused, by the World's refusalCode (cc-platform #1335, world-member-change-v2). A code this
 * Desk does not know reads as the general refusal, so a new one needs no Desk release.
 */
const MEMBER_CHANGE_DECLINED_WORDS = "You declined this change, and nothing was changed.";
/** The email codes, written by the World only on a change that sends an email (an email invitation or a resend). */
const MEMBER_CHANGE_EMAIL_REFUSED_WORDS: ReadonlyMap<string, string> = new Map([
  [
    "RESEND_REFUSED",
    "The email service refused to send this invitation, so it was not sent. Ask Cleo to prepare it again.",
  ],
  [
    "WORLD_MEMBER_SIGN_IN_ENDED",
    "The sign-in that approved this change ended before the email was sent, so it was not sent. Ask Cleo to prepare it again.",
  ],
  [
    "GATE_MESSAGE_STOPPED",
    "You stopped this request before the email was sent, so it was not sent.",
  ],
]);
const MEMBER_CHANGE_SENDS_EMAIL: ReadonlySet<string> = new Set(["invite-email", "resend"]);
/** A Map, never an object's keys: a code such as "__proto__" or "constructor" is only a code this Desk does not know. */
const memberChangeRefusedWords = (change: { kind: string; refusalCode?: string }): string => {
  if (change.refusalCode === "GATE_DECLINED") return MEMBER_CHANGE_DECLINED_WORDS;
  const email =
    change.refusalCode === undefined || !MEMBER_CHANGE_SENDS_EMAIL.has(change.kind)
      ? undefined
      : MEMBER_CHANGE_EMAIL_REFUSED_WORDS.get(change.refusalCode);
  return email ?? MEMBER_CHANGE_STATUS_WORDS.refused;
};

/**
 * Stop a World's subscription renewing, as the person reads it. Every fact is
 * the stored plan's, read from the World (context.subscriptionPlan); Cleo's own
 * reason is shown apart, labelled as hers, and is never a fact. Until the plan
 * has loaded and matches the call, Approve is blocked and the reason is said.
 */
function plainSubscriptionCancel(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const planKey = typeof input["planKey"] === "string" ? input["planKey"] : null;
  const snapshotDigest =
    typeof input["snapshotDigest"] === "string" ? input["snapshotDigest"] : null;
  const base = { canvasId: null, perCallOnly: true } as const;
  const blocked = (headline: string, why: string): PlainApprovalRequest => ({
    ...base,
    headline,
    rows: [["Why you cannot approve yet", why]],
    subscriptionPlan:
      planKey !== null && snapshotDigest !== null ? { planKey, snapshotDigest } : null,
    approvalBlocked: why,
  });
  if (planKey === null || snapshotDigest === null)
    return blocked(
      "Stop a World's subscription renewing",
      "This request does not name a stored plan, so it cannot be shown or approved.",
    );
  const read = context.subscriptionPlan ?? { status: "reading" as const };
  if (read.status === "reading")
    return blocked(
      "Reading the plan from Neucleos…",
      "Reading the plan from Neucleos. Approve unlocks when it has loaded.",
    );
  if (read.status === "failed")
    return blocked(
      "Cannot show what this would change",
      `${read.reason} Nothing can be approved until it can be read.`,
    );
  const { plan } = read;
  if (plan.planKey !== planKey || plan.snapshotDigest !== snapshotDigest)
    return blocked(
      "Cannot show what this would change",
      "The stored plan is not the one this request names. Ask Cleo to preview the subscription again.",
    );
  if (plan.expiresAt <= Date.now())
    return blocked(
      "Cannot show what this would change",
      "This plan has expired. Ask Cleo to preview the subscription again.",
    );
  const s = plan.snapshot;
  const end = subscriptionDate(s.currentPeriodEnd);
  const per = s.intervalCount === 1 ? `/${s.interval}` : ` every ${s.intervalCount} ${s.interval}s`;
  const price = `${subscriptionMoney(s.amountCents * s.quantity, s.currency)}${per}`;
  const suspended = s.targetWorldStatus === "suspended";
  const pastDue = s.subscriptionStatus === "past_due";
  const access = [
    suspended
      ? `Currently suspended.${pastDue ? " Its latest payment is past due." : ""}`
      : pastDue
        ? "Working, but its latest payment is past due."
        : "Working normally.",
  ];
  // A suspended World is not "working until" anything: say what is true of it.
  const effect = suspended
    ? `Stops renewing on ${end}; it stays suspended: scheduling does not suspend access or restore it. Nothing is refunded.`
    : `Stops renewing; keeps working until ${end}; scheduling does not suspend access. Nothing is refunded.`;
  const headline =
    suspended || pastDue
      ? `Stop ${s.targetWorldName}'s ${price} plan renewing on ${end}.`
      : `Stop ${s.targetWorldName}'s ${price} plan renewing on ${end}. It keeps working until then.`;
  const reason = typeof input["reason"] === "string" ? input["reason"].trim() : "";
  return {
    ...base,
    headline,
    rows: [
      ["World", s.targetWorldName],
      ["Plan", `${price}${s.quantity > 1 ? `, quantity ${s.quantity}` : ""}`],
      ["Renews", `${end}; it will not renew.`],
      ["Access now", access.join(" ")],
      ["Effect", effect],
      ...(reason === "" ? [] : [["Cleo's reason (her words)", reason] as const]),
    ],
    subscriptionPlan: { planKey, snapshotDigest },
    approvalBlocked: null,
  };
}

/**
 * Where a booking type's standing meeting link goes, as the card says it: a new type's bookings. The World's own words
 * (backend lib/meetingUrl.js), which the consulting catalog card (PR #1625) also states.
 */
export { MEETING_LINK_AUDIENCE_WORDS };
/** The same for a link set on a type that may already have bookings. */
export const MEETING_LINK_CHANGE_AUDIENCE_WORDS =
  "Emailed with every booking of this type to the person who booked and to you: the confirmation, a move, and the 24-hour and 1-hour reminders, including the reminders of bookings already made. Never shown on the public booking page.";

/** An argument exactly as the call carries it, never cut: a described call hides nothing. */
function exactArgumentText(value: unknown): string {
  return typeof value === "string" ? value : (JSON.stringify(value) ?? String(value));
}
/** Every argument the plain rows have not already said, by its own name and exact value. */
function remainingArguments(
  input: Record<string, unknown>,
  said: ReadonlyArray<string>,
): Array<readonly [string, string]> {
  return Object.entries(input)
    .filter(([key]) => !said.includes(key))
    .map(([key, value]) => [key, exactArgumentText(value)] as const);
}
const validMeetingLink = (link: unknown): boolean => "url" in checkMeetingUrl(link);
/** The link as the World will store and send it (backend lib/meetingUrl.js), or why it will refuse it. */
function meetingLinkText(link: unknown): string {
  const checked = checkMeetingUrl(link);
  return "url" in checked
    ? checked.url
    : `${exactArgumentText(link)} (this link will be refused: it ${checked.problem})`;
}

/**
 * A booking type's meeting link, or a consulting terms revision's sales-tax
 * policy, as the person approving it reads it: the link in full exactly as it
 * will be stored and sent, never cut; the policy in plain words, with who is
 * refused; and then every other argument of the call, exactly. Only a call
 * that names a link or a policy is described here. Each tool is named once,
 * at its own branch (law 10).
 */
function plainBookingLinkOrTaxPolicy(
  tool: string,
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest | null {
  if (tool === "neucleos_publish_consulting_terms") {
    const policy = input["salesTaxPolicy"];
    if (!(CONSULTING_TAX_POLICIES as readonly unknown[]).includes(policy)) return null;
    return {
      headline: "Publish new consulting terms",
      rows: [
        ["Sales tax", `${CONSULTING_TAX_POLICY_CARD_WORDS[policy as ConsultingTaxPolicy]}.`],
        ["Policy", String(policy)],
        [
          "Applies to",
          "Purchases made after this revision. Purchases already made keep the terms they accepted.",
        ],
        ...remainingArguments(input, ["salesTaxPolicy"]),
      ],
      canvasId: null,
    };
  }
  if (tool !== "neucleos_create_booking_type" && tool !== "neucleos_set_booking_type_rules")
    return null;
  if (!("meetingUrl" in input)) return null;
  const link = input["meetingUrl"];
  if (tool === "neucleos_create_booking_type") {
    const title = typeof input["title"] === "string" ? input["title"] : "untitled";
    return {
      headline: `Publish the booking page “${title}” with a meeting link`,
      rows: [
        ["Meeting link", meetingLinkText(link)],
        ...(validMeetingLink(link) ? [["Sent to", MEETING_LINK_AUDIENCE_WORDS] as const] : []),
        ...remainingArguments(input, ["meetingUrl"]),
      ],
      canvasId: null,
    };
  }
  const bookingTypeId = typeof input["bookingTypeId"] === "string" ? input["bookingTypeId"] : null;
  if (bookingTypeId === null) return null;
  // The type as the host's own list carries it now: its name and the link it sends today.
  const known = context.bookingType ?? null;
  const name = known?.status === "loaded" ? `“${known.title}”` : "this booking type";
  return {
    headline:
      link === null ? `Remove the meeting link from ${name}` : `Set the meeting link for ${name}`,
    rows: [
      [
        "Booking type",
        known?.status === "loaded" ? `${known.title} (${bookingTypeId})` : bookingTypeId,
      ],
      [
        "Link now",
        known?.status === "loaded"
          ? (known.meetingUrl ?? "None.")
          : known?.status === "failed"
            ? "Could not be read; the World keeps the link it has until this change is saved."
            : "Not read yet.",
      ],
      [
        link === null ? "Meeting link" : "New link",
        link === null ? "Removed: bookings are sent no meeting link." : meetingLinkText(link),
      ],
      ...(validMeetingLink(link) ? [["Sent to", MEETING_LINK_CHANGE_AUDIENCE_WORDS] as const] : []),
      ...remainingArguments(input, ["bookingTypeId", "meetingUrl"]),
    ],
    canvasId: null,
    bookingType: { bookingTypeId },
  };
}

export const SAVE_CONSULTANT_PROFILE_TOOL = "neucleos_save_consultant_profile";
/**
 * PR #1625: the one title the Desk's own server puts in front of a change of who can buy (a profile save naming
 * `audience`) (apps/server/src/provider/worldGateBridge.ts CATALOG_CHANGE_PREVIEW_TITLE; a test reads that file).
 * W-dc12e842: it is display text only; a card is never recognised by it (or by any text) as that call.
 */
export const CATALOG_CHANGE_PREVIEW_TITLE = "Change who can buy your consulting packages";
const CATALOG_CHANGE_UNSHOWN =
  "This request changes who can buy your consulting packages but does not show its catalog card here. Ask Cleo to ask again; the Desk shows the card when the request carries its arguments.";
const catalogChangeUnshown = (whole: string) => ({
  tool: SAVE_CONSULTANT_PROFILE_TOOL,
  fields: null,
  raw: whole,
  plain: {
    headline: "Change who can buy from you",
    rows: [["Why you cannot approve yet", CATALOG_CHANGE_UNSHOWN]] as ReadonlyArray<
      readonly [string, string]
    >,
    canvasId: null,
    perCallOnly: true,
    approvalBlocked: CATALOG_CHANGE_UNSHOWN,
  },
});
const CATALOG_HEADLINE: Readonly<Record<ConsultingAudience, string>> = {
  invited: "Sell only to the buyers you invite",
  public: "Sell to everyone",
};

/**
 * PR #1625: an audience change (who can see and buy a consultant's packages) as the person approving it reads it: the
 * World's catalog card for that audience, every line exactly as the World wrote it (seller and payment account,
 * provider, who can buy, packages and prices, hourly purchase, terms, sales tax, delivery, later changes, the card's
 * digest), then every other argument exactly. Approve stays blocked until the card has loaded and its digest is the
 * call's catalogDigest: what the person sees is what the World will save, or nothing is approved.
 */
function plainConsultingCatalog(
  input: Record<string, unknown>,
  context: ApprovalRequestContext,
): PlainApprovalRequest {
  const audience: ConsultingAudience | null =
    input["audience"] === "public" || input["audience"] === "invited" ? input["audience"] : null;
  const catalogDigest = typeof input["catalogDigest"] === "string" ? input["catalogDigest"] : null;
  const ref = audience !== null && catalogDigest !== null ? { audience, catalogDigest } : null;
  const base = { canvasId: null, perCallOnly: true } as const;
  const others = remainingArguments(input, ["audience", "catalogDigest"]);
  const blocked = (
    headline: string,
    why: string,
    lines: ReadonlyArray<readonly [string, string]> = [],
  ): PlainApprovalRequest => ({
    ...base,
    headline,
    rows: [["Why you cannot approve yet", why], ...lines, ...others],
    consultingCatalog: ref,
    approvalBlocked: why,
  });
  if (ref === null)
    return blocked(
      "Change who can buy from you",
      "This request does not name the catalog card it was prepared from, so it cannot be shown or approved.",
    );
  const read = context.consultingCatalog ?? { status: "reading" as const };
  if (read.status === "reading")
    return blocked(
      "Reading the catalog card…",
      "Reading what a buyer agrees to from the World. Approve unlocks when it has loaded.",
    );
  if (read.status === "failed")
    return blocked(
      "Cannot show the catalog card",
      `${read.reason} Nothing can be approved until it can be read.`,
    );
  const card = read.cards[ref.audience];
  if (card.digest !== ref.catalogDigest)
    return blocked(
      "The catalog changed",
      "What a buyer agrees to changed since Cleo read it, so this request no longer matches the card below. Ask Cleo to read your consulting setup again and ask again.",
      card.lines,
    );
  return {
    ...base,
    headline: CATALOG_HEADLINE[ref.audience],
    rows: [...card.lines, ...others],
    consultingCatalog: ref,
    approvalBlocked: null,
  };
}

/**
 * A call as the person reads it: the tool, then each argument on its own line, and in plain words where it is
 * described. W-dc12e842: plain words come only from the structured call of the Desk's own server (deskOwnToolCall);
 * the request's text is shown, never read for which tool runs. Any other call keeps its tool and arguments, from
 * its structured identity, and no plain words; a request with no structured call shows its text alone.
 */
export function describedApprovalRequest(
  request: ApprovalRequestInput,
  context: ApprovalRequestContext = {},
): {
  readonly tool: string;
  readonly fields: ReadonlyArray<readonly [string, string]> | null;
  readonly raw: string;
  readonly plain: PlainApprovalRequest | null;
} {
  const whole = request.detail;
  const call = request.call ?? null;
  const own = deskOwnToolCall(call);
  const args = own?.args ?? (call?.kind === "tool" ? call.args : undefined);
  const tool = own?.tool ?? calledTool(call) ?? "";
  if (typeof args !== "object" || args === null || Array.isArray(args)) {
    // PR #1625: the Desk's own profile save that does not carry its arguments cannot show the catalog card, so it
    // cannot be approved here.
    if (own?.tool === SAVE_CONSULTANT_PROFILE_TOOL) return catalogChangeUnshown(whole);
    return { tool, fields: null, raw: whole, plain: null };
  }
  const record = args as Record<string, unknown>;
  const plain = own === null ? null : plainToolCall(own.tool, record, context, own.preview);
  const zone = context.timeZone ?? deviceTimeZone();
  const fields = Object.entries(record).map(([key, value]) => {
    // The exact value stays; a time also reads as a date and time (W-96c0e692).
    const time =
      TIME_FIELDS.has(key) && value !== null && typeof value !== "string"
        ? approvalTimeText(value, zone)
        : null;
    return [
      key,
      time === null ? approvalFieldText(value) : `${approvalFieldText(value)} = ${time}`,
    ] as const;
  });
  return { tool, fields, raw: JSON.stringify(record), plain };
}

/** The Desk's own call (deskOwnToolCall) in plain words, by its tool, or null when it is not described here. */
function plainToolCall(
  tool: string,
  record: Record<string, unknown>,
  context: ApprovalRequestContext,
  preview: { readonly title: string } | null,
): PlainApprovalRequest | null {
  // Can 47 / Can 68: which models a World can use, in the same words as Pocket's card (@cleo/shared/modelAccessWords).
  const modelAccess = plainModelAccessCall(tool, record, context.cardFacts);
  if (modelAccess) return { ...modelAccess, canvasId: null };
  // Can 47: a Canvas create or save in a few lines, in the same words as Pocket's card (@cleo/shared/canvasWriteWords); the
  // exact request, the page's files included, stays under "The exact request".
  if (
    tool === "neucleos_canvas" &&
    (record["action"] === "create" || record["action"] === "save")
  ) {
    const write = plainCanvasWrite(record);
    if (write) return { ...write, canvasId: null };
  }
  // #1719: the website pages card, for the Desk's own neucleos_canvas call (structured, from any engine).
  if (tool === "neucleos_canvas" && record["action"] === WEBSITE_PAGES_ACTION)
    return plainWebsitePages(record, context);
  // Step 3 K2c2 (#1844): the platform Components release card, for the Desk's own neucleos_canvas call.
  if (tool === "neucleos_canvas" && record["action"] === COMPONENT_RELEASE_ACTION)
    return plainComponentRelease(record, context);
  // Step 3 I2: a live summary on a website, approved only on this card (aggregateDatasetCard.ts).
  if (tool === "neucleos_canvas" && record["action"] === AGGREGATE_DATASET_ACTION) {
    const card = aggregateDatasetCard(record);
    return {
      headline: card.headline,
      rows: card.rows,
      canvasId: null,
      perCallOnly: true,
      ...(card.blocked === null ? { aggregateDataset: true } : { approvalBlocked: card.blocked }),
    };
  }
  if (tool === "neucleos_canvas" && record["action"] === "canvas-share")
    return plainCanvasShare(record, context);
  if (tool === "neucleos_canvas" && record["action"] === "canvas-friend-link")
    return plainCanvasFriendLink(record, context);
  if (tool === "neucleos_canvas" && record["action"] === "component-create")
    return plainComponentCreate(record);
  if (tool === SAVE_ATTACHMENT_TOOL) return plainAttachmentToMedia(record);
  if (tool === SET_SETTING_TOOL) return plainSettingChange(record);
  if (tool === SAVE_APPEARANCE_TOOL) return plainAppearanceSave(record);
  if (tool === "neucleos_send_conversation_message")
    return plainMessageSend(record, context, preview);
  if (tool === "neucleos_crm") return plainCrmWrite(record, context);
  if (tool === SUBSCRIPTION_SCHEDULE_TOOL) return plainSubscriptionCancel(record, context);
  if (tool === APPLY_COMMERCIAL_CHANGE_TOOL) return plainCommercialChange(record, context);
  if (tool === APPLY_MEMBER_CHANGE_TOOL) return plainMemberChange(record, context);
  // Can 81: a coding job, approved only on this card, built from the call alone (codingJobCard.ts).
  if (tool === START_CODING_JOB_TOOL) {
    const card = codingJobCard(record);
    return {
      headline: card.headline,
      rows: card.rows,
      canvasId: null,
      perCallOnly: true,
      ...(card.blocked === null ? { codingJob: true } : { approvalBlocked: card.blocked }),
    };
  }
  if (tool === SAVE_CONSULTANT_PROFILE_TOOL && "audience" in record)
    return plainConsultingCatalog(record, context);
  return (
    plainCalendarWrite(tool, record, context) ?? plainBookingLinkOrTaxPolicy(tool, record, context)
  );
}

export async function listGateInstances(
  worldKey: string,
  state: GateInstanceState = "CREATED",
  fetchImpl: typeof fetch = fetch,
  cursor: string | null = null,
): Promise<GateInstanceListOutcome> {
  const outcome = await call(
    "/api/neucleos/world/gate-instances",
    { worldKey, state, limit: 50, cursor },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const raw = outcome["rows"];
  if (!Array.isArray(raw)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The gate-instances lane returned an invalid list.",
    };
  }
  const listState = typeof outcome["state"] === "string" ? outcome["state"] : state;
  const rows: GateInstanceRow[] = [];
  for (const value of raw) {
    const row = parsedGateInstanceRow(value, listState);
    if (row === null) {
      return {
        ok: false,
        kind: "transport",
        reason: "The gate-instances lane returned an invalid row.",
      };
    }
    rows.push(row);
  }
  return {
    ok: true,
    rows,
    nextCursor: typeof outcome["nextCursor"] === "string" ? outcome["nextCursor"] : null,
    attentionCount:
      typeof outcome["attentionCount"] === "number"
        ? outcome["attentionCount"]
        : rows.filter((row) => row.needsAttention).length,
  };
}

/**
 * The person's decision, carrying the EXACT targetDigest the instance was
 * opened with and a fresh decisionKey per click so a double-click can never
 * decide twice. The lane's typed refusal (digest mismatch, already decided,
 * stale grant) comes back as a value.
 */
export async function decideGateInstance(
  input: {
    readonly worldKey: string;
    readonly gateInstanceKey: string;
    readonly targetDigest: string;
    readonly outcome: GateDecisionOutcome;
    readonly decisionKey: string;
    /** Decision 7: his answer to a coding agent's question (a choice, a note); the World alone checks it. */
    readonly answer?: { readonly choice?: string; readonly note?: string };
  },
  fetchImpl: typeof fetch = fetch,
): Promise<GateInstanceDecideOutcome> {
  // W-dd5db5e2: an add, a removal or a role change is approved with the person's Touch ID for that exact change first
  // (server memberTouchIdApproval.ts); the World records it, and the approval below spends it. A Mac that cannot ask (no
  // Touch ID, no helper, no key yet) sends the approval as before, and the World decides whether it needs one. Codex P2
  // on #3017: so does any other failure to record it (a signer or World that refused it): the World's switch, not this
  // Desk, decides whether the approval needs the presence. Only the person's own close of the Touch ID sheet stops it.
  if (
    input.outcome === "approved" &&
    memberApprovalNeedsTouchId(input.worldKey, input.gateInstanceKey)
  ) {
    const presence = await call(
      "/api/neucleos/members/change/approve-touch-id",
      { worldKey: input.worldKey, gateInstanceKey: input.gateInstanceKey },
      fetchImpl,
    );
    if (isRefusal(presence) && presence.kind === "touch_id_cancelled") return presence;
  }
  // W-dd5db5e2: an Approve of a change to who belongs to a World sends the receipt its Members card's read was given.
  const cardReceipt =
    input.outcome === "approved"
      ? await memberCardReceiptFor(input.worldKey, input.gateInstanceKey, fetchImpl)
      : null;
  const answer = await call(
    "/api/neucleos/world/gate-instances/decide",
    { ...input, ...(cardReceipt ? { cardReceipt } : {}) },
    fetchImpl,
  );
  if (isRefusal(answer)) return answer;
  if (cardReceipt) spendMemberCardReceipt(cardReceipt);
  const invitationLink = parsedInvitationLink(answer["memberLink"]);
  return {
    ok: true,
    row: parsedGateInstanceRow(answer["row"]),
    ...(invitationLink ? { invitationLink } : {}),
  };
}

// ---- the Cleo-mark badge: awaiting instances, folded to a count ----------

export const GATE_AWAITING_POLL_MS = 30_000;
const awaitingListeners = new Set<() => void>();

export function subscribeGateInstancesChanged(listener: () => void): () => void {
  awaitingListeners.add(listener);
  return () => {
    awaitingListeners.delete(listener);
  };
}

/** The rail calls this after a decision lands; every badge refetches. */
export function notifyGateInstancesChanged(): void {
  for (const listener of awaitingListeners) listener();
}

/**
 * The count of gate instances awaiting THIS person in the World — the
 * Cleo-mark badge on the pinned Inbox tab. From the lane, not the local
 * liveness fold: a headless session's gate is a row here whether or not
 * its thread is live on this Desk (SP41). A refusal is an honest zero.
 */
export function useWorldGateInstancesAwaiting(worldKey: string | null): number {
  const rows = useNotificationSnapshots(worldKey ? [worldKey] : []);
  return rows[0]?.value?.pendingGateCount ?? rows[0]?.value?.gates?.length ?? 0;
}
